import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputBase,
  InputAdornment,
  Button,
  IconButton,
  Collapse,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import { alpha } from "@mui/material/styles";
import { getComplianceIcon } from "../../assets/images/compliance/IconCompliance.jsx";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Right-side preview panel width (px); same pattern as Offline CUR upload manual panel. */
const REMEDIATION_PREVIEW_PANEL_WIDTH_PX = 600;

const isHttpUrlString = (v) => typeof v === "string" && /^https?:\/\//i.test(v.trim());

/**
 * In-app “View remediation” action: parent opens a portaled side panel with the URL in an iframe.
 * Falls back to a new tab if the URL is not http(s) or if `onOpenPreview` is omitted.
 */
function RemediationPreviewLink({ url, onOpenPreview, sx: sxProp = {} }) {
  if (!url) return null;
  const isHttp = isHttpUrlString(url);
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onOpenPreview === "function" && isHttp) onOpenPreview(url);
    else window.open(String(url), "_blank", "noopener,noreferrer");
  };
  return (
    <Typography
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        display: "inline",
        m: 0,
        p: 0,
        border: "none",
        background: "none",
        fontFamily: t.fontFamily,
        fontSize: "0.8125rem",
        fontWeight: 500,
        color: t.colors.accent,
        cursor: "pointer",
        textAlign: "left",
        textDecoration: "underline",
        textUnderlineOffset: "2px",
        "&:hover": {
          color: t.colors.accentHover,
          textDecoration: "underline",
        },
        ...sxProp,
      }}
    >
      View remediation
    </Typography>
  );
}

/** Keep Select menus in-dialog so clicks are not treated as outside the modal. */
const COMPLIANCE_DIALOG_SELECT_MENU_PROPS = {
  disablePortal: true,
  disableScrollLock: true,
  slotProps: {
    paper: {
      sx: { maxHeight: 320 },
    },
  },
};

/** Unique framework keys from a row’s `compliance_remediations` (same labeling as the grouped list). */
function dialogFrameworkListFromRow(row) {
  if (!row || !Array.isArray(row.compliance_remediations)) return [];
  const s = new Set();
  row.compliance_remediations.forEach((r) => {
    const raw = r?.framework;
    const fw = raw != null && String(raw).trim() !== "" ? String(raw) : "—";
    s.add(fw);
  });
  return Array.from(s).sort((a, b) => a.localeCompare(b));
}

/** Distinct control IDs in a set of remediation items (strings, sorted with numeric awareness). */
function controlIdOptionsFromRemediationItems(items) {
  const ids = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const list = Array.isArray(item?.control_ids) ? item.control_ids : [];
    list.forEach((c) => {
      if (c != null && String(c).trim() !== "") ids.add(String(c));
    });
  });
  return Array.from(ids).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function sameFrameworkValue(a, b) {
  return String(a == null ? "" : a).trim() === String(b == null ? "" : b).trim();
}

function sameControlIdValue(a, b) {
  return String(a == null ? "" : a).trim() === String(b == null ? "" : b).trim();
}

/** Stable default so hooks do not see a new `[]` every render when prop is omitted. */
const DEFAULT_MODULE_FILTERS = [];

/** Widths sum to 100%; Security and Compliance share equal share to avoid a wide empty gap. */
const DETAIL_TABLE_COLUMNS = [
  { id: "arn", label: "ARN(s)", desc: "Amazon Resource Name — unique identifier for the resource", width: "26%", minWidth: 200 },
  { id: "service", label: "Service", desc: "AWS service or product (e.g. S3, IAM, EC2)", width: "10%" },
  { id: "region", label: "Region", desc: "AWS region where the resource is located", width: "9%" },
  { id: "security_failed_checks", label: "Security", desc: "Security findings — click for remediations", width: "11%" },
  {
    id: "compliance_frameworks",
    label: "Compliance",
    desc: "Compliance Findings, click for remediations",
    width: "11%",
    align: "center",
  },
  {
    id: "compliance_control_id_count",
    label: "Control ID",
    desc: "Control ID Findings, click for remediations",
    width: "10%",
    minWidth: 88,
    align: "center",
  },
  {
    id: "cloud_waste",
    label: "Cloud Waste",
    desc: "Estimated Monthly Savings, hover for recommended actions",
    width: "10%",
    align: "center",
  },
  { id: "cost_unblended_mtd", label: "Cost (Last 30 Days)", desc: "Unblended cost for this resource in the last 30 days", width: "13%", align: "right" },
];

function formatCost(value) {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/** Frameworks with findings — matches the number shown on the Compliance chip. */
function complianceFrameworksCount(row) {
  const failedByFramework = row?.compliance_failed_controls;
  if (failedByFramework && typeof failedByFramework === "object" && !Array.isArray(failedByFramework)) {
    return Object.keys(failedByFramework).length;
  }
  return 0;
}

/** Count control IDs from `compliance_failed_controls` values (arrays, numbers, or nested control_ids). */
function countControlsFromFailedMap(map) {
  if (!map || typeof map !== "object" || Array.isArray(map)) return 0;
  return Object.values(map).reduce((sum, val) => {
    if (Array.isArray(val)) return sum + val.length;
    if (typeof val === "number" && Number.isFinite(val)) return sum + val;
    if (val && typeof val === "object" && Array.isArray(val.control_ids)) return sum + val.control_ids.length;
    if (typeof val === "string" && val.trim()) return sum + 1;
    return sum;
  }, 0);
}

/**
 * Total control IDs: prefer sum of control_ids on compliance_remediations; if none, use failed-controls map;
 * last resort compliance_failed_total (API aggregate when detail arrays are omitted).
 */
function countRemediationControlIds(row) {
  const list = Array.isArray(row?.compliance_remediations) ? row.compliance_remediations : [];
  const fromRem = list.reduce((sum, item) => {
    const ids = Array.isArray(item?.control_ids) ? item.control_ids : [];
    return sum + ids.length;
  }, 0);
  if (fromRem > 0) return fromRem;
  const fromMap = countControlsFromFailedMap(row?.compliance_failed_controls);
  if (fromMap > 0) return fromMap;
  return Number(row?.compliance_failed_total) || 0;
}

/** Group compliance remediation list entries by `framework` (stable sorted keys). */
function groupComplianceRemediationsByFramework(items) {
  const map = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const raw = item?.framework;
    const fw = raw != null && String(raw).trim() !== "" ? String(raw) : "—";
    if (!map.has(fw)) map.set(fw, []);
    map.get(fw).push(item);
  });
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}

/** One nested table row per control id (splits `control_ids` with multiple values). */
function expandRemediationItemsToControlRows(items) {
  const rows = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const ids = Array.isArray(item?.control_ids)
      ? item.control_ids.filter((x) => x != null && String(x).trim() !== "")
      : [];
    if (ids.length === 0) {
      rows.push({ controlId: "—", item });
    } else {
      ids.forEach((cid) => rows.push({ controlId: String(cid), item }));
    }
  });
  return rows;
}

/** Client refinement when API sends repeated `module` filters (AND semantics). */
function rowMatchesModuleClientFilter(row, moduleFilters) {
  if (!Array.isArray(moduleFilters) || moduleFilters.length === 0) return true;
  return moduleFilters.every((part) => {
    if (part === "security") return Number(row.security_failed_checks) > 0;
    if (part === "compliance") return Number(row.compliance_failed_total) > 0;
    if (part === "cost") return Number(row.cost_unblended_mtd) > 0;
    if (part === "cloud_waste") return row.cloud_waste === true;
    return true;
  });
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const parts = String(dateString).split("-");
    if (parts.length >= 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        return new Date(y, m, d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    return String(dateString);
  } catch {
    return String(dateString);
  }
}

/** Shadcn-style tooltip: header + list (shown on hover) */
const complianceTooltipSx = {
  fontFamily: t.fontFamily,
  maxWidth: 320,
  padding: 0,
  borderRadius: t.radius.lg,
  border: `1px solid ${t.colors.border}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.02)",
  backgroundColor: t.colors.card,
  // Allow inner content (like scrollable lists) to render fully
  overflow: "visible",
};

/** Compliance cell: status chip or count; click opens modal in parent via onOpenDetails */
function ComplianceCell({ row, onOpenDetails, onOpenRemediationPreview }) {
  const remediations = Array.isArray(row.compliance_remediations)
    ? row.compliance_remediations
    : [];
  const hasRemediations = remediations.length > 0;
  const totalControls = Number(row.compliance_failed_total) || 0;
  const frameworksCount = complianceFrameworksCount(row);

  const statusLower = (row.compliance_status || "").toLowerCase();
  if (statusLower === "unknown" || statusLower === "unknow") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            color: t.colors.textMuted,
          }}
        >
          —
        </Typography>
      </Box>
    );
  }

  const tooltipContent = hasRemediations ? (
    <Box sx={{ fontFamily: t.fontFamily, maxWidth: 360 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: t.colors.text,
          }}
        >
          Compliance remediations
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: t.colors.textMuted,
            mt: 0.25,
          }}
        >
          {frameworksCount} framework{frameworksCount === 1 ? "" : "s"} ·{" "}
          {totalControls} failed control{totalControls === 1 ? "" : "s"} ·{" "}
          {remediations.length} remediation item
          {remediations.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 260, overflowY: "auto", py: 1 }}>
        {remediations.map((item, idx) => {
          const iconSrc = getComplianceIcon(item.framework);
          return (
            <Box
              key={`${item.framework || "fw"}-${idx}`}
              sx={{
                px: 2,
                py: 0.75,
                borderBottom: `1px solid ${t.colors.borderLight}`,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {iconSrc && (
                <Box
                  component="img"
                  src={iconSrc}
                  alt={item.framework}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    objectFit: "contain",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: t.colors.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    mb: 0.25,
                  }}
                >
                  {item.framework || "Framework"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: t.colors.text,
                    mb: 0.25,
                  }}
                >
                  {item.title}
                </Typography>
                {item.url && (
                  <Box sx={{ mb: 0.25, display: "block" }}>
                    <RemediationPreviewLink
                      url={item.url}
                      onOpenPreview={onOpenRemediationPreview}
                      sx={{ fontSize: "0.72rem", display: "inline-block" }}
                    />
                  </Box>
                )}
                {Array.isArray(item.control_ids) && item.control_ids.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: t.colors.textMuted,
                    }}
                  >
                    Controls: {item.control_ids.join(", ")}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  ) : null;

  const isUnknown =
    (row.compliance_status || "").toLowerCase() === "unknown" ||
    (row.compliance_status || "").toLowerCase() === "unknow";
  const chip = isUnknown ? (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontFamily: t.fontFamily,
        color: t.colors.textMuted,
      }}
    >
      —
    </Typography>
  ) : (
    <StatusChip
      status={row.compliance_status}
      label={String(frameworksCount)}
    />
  );

  if (typeof onOpenDetails === "function") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails(row);
          }}
          sx={{
            all: "unset",
            cursor: hasRemediations ? "pointer" : "default",
            display: "inline-flex",
            justifyContent: "center",
          }}
        >
          {chip}
        </Box>
      </Box>
    );
  }

  if (tooltipContent) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Tooltip
          title={tooltipContent}
          placement="top-start"
          enterDelay={200}
          leaveDelay={0}
          componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
        >
          <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
            {chip}
          </Box>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>{chip}</Box>
  );
}

/** Control ID total in the same StatusChip style as Compliance; click opens the compliance details dialog. */
function ControlIdCell({ row, onOpenDetails }) {
  const count = countRemediationControlIds(row);
  const remediations = Array.isArray(row.compliance_remediations) ? row.compliance_remediations : [];
  const hasRemediations = remediations.length > 0;
  const canOpen = typeof onOpenDetails === "function";

  const statusLower = (row.compliance_status || "").toLowerCase();
  if (statusLower === "unknown" || statusLower === "unknow") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography sx={{ fontSize: "0.75rem", fontFamily: t.fontFamily, color: t.colors.textMuted }}>—</Typography>
      </Box>
    );
  }

  const chip = <StatusChip status={row.compliance_status} label={String(count)} />;

  if (!canOpen) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>{chip}</Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDetails(row);
        }}
        sx={{
          all: "unset",
          cursor: hasRemediations ? "pointer" : "default",
          display: "inline-flex",
        }}
      >
        {chip}
      </Box>
    </Box>
  );
}

function StatusChip({ status, label }) {
  const isFailed = (status || "").toLowerCase() === "failed";
  return (
    <Chip
      size="small"
      label={label ?? (isFailed ? "Failed" : "Passed")}
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        fontFamily: t.fontFamily,
        borderRadius: t.radius.sm,
        height: 20,
        ...(isFailed
          ? { bgcolor: t.colors.destructiveBg, color: t.colors.destructive, border: `1px solid ${t.colors.destructiveBorder}` }
          : { bgcolor: t.colors.successBg, color: t.colors.success }),
      }}
    />
  );
}

const SECURITY_SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const SECURITY_SEVERITY_RANK = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};
const SECURITY_SEVERITY_COLORS = {
  CRITICAL: t.colors.severity.critical,
  HIGH: t.colors.severity.high,
  MEDIUM: t.colors.severity.medium,
  LOW: t.colors.severity.low,
};

function securitySeverityDisplay(severity) {
  if (severity == null || severity === "") return null;
  const upper = String(severity).trim().toUpperCase();
  const label = upper.charAt(0) + upper.slice(1).toLowerCase();
  return { label, color: SECURITY_SEVERITY_COLORS[upper] || t.colors.textMuted };
}

function sortSecurityRemediationsList(remediations) {
  return [...remediations].sort((a, b) => {
    const aKey = String(a?.severity || "").trim().toUpperCase();
    const bKey = String(b?.severity || "").trim().toUpperCase();
    const aRank = SECURITY_SEVERITY_RANK[aKey] ?? Number.MAX_SAFE_INTEGER;
    const bRank = SECURITY_SEVERITY_RANK[bKey] ?? Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    const aId = String(a?.check_id || a?.checkId || a?.title || "");
    const bId = String(b?.check_id || b?.checkId || b?.title || "");
    return aId.localeCompare(bId);
  });
}

/**
 * Toolbar: severity select (All + counts; only severities with items) + title search.
 * Table: plain column headers + data rows.
 */
function SecurityRemediationsDialogTable({ row, onOpenRemediationPreview }) {
  const [severityFilter, setSeverityFilter] = useState("");
  const [titleQuery, setTitleQuery] = useState("");

  const sortedRemediations = useMemo(() => {
    const raw = Array.isArray(row?.security_remediations) ? row.security_remediations : [];
    return sortSecurityRemediationsList(raw);
  }, [row]);

  const severityCounts = useMemo(() => {
    const counts = { ALL: sortedRemediations.length };
    SECURITY_SEVERITY_ORDER.forEach((sev) => {
      counts[sev] = 0;
    });
    sortedRemediations.forEach((item) => {
      const u = String(item?.severity || "").trim().toUpperCase();
      if (Object.prototype.hasOwnProperty.call(counts, u)) {
        counts[u] += 1;
      }
    });
    return counts;
  }, [sortedRemediations]);

  const filteredRows = useMemo(() => {
    const q = (titleQuery || "").trim().toLowerCase();
    return sortedRemediations.filter((item) => {
      const u = String(item?.severity || "").trim().toUpperCase();
      if (severityFilter && u !== severityFilter) return false;
      if (q) {
        const title = String(item?.title || "").toLowerCase();
        const checkId = String(item?.check_id || item?.checkId || "").toLowerCase();
        if (!title.includes(q) && !checkId.includes(q)) return false;
      }
      return true;
    });
  }, [sortedRemediations, severityFilter, titleQuery]);

  const formatSevLabel = (sev) => (sev ? sev.charAt(0) + sev.slice(1).toLowerCase() : "");
  const severityMenuLabel = (sevKey) => {
    if (!sevKey) return `All (${severityCounts.ALL})`;
    const n = severityCounts[sevKey] ?? 0;
    return `${formatSevLabel(sevKey)} (${n})`;
  };

  const severitiesWithItems = useMemo(
    () => SECURITY_SEVERITY_ORDER.filter((sev) => (severityCounts[sev] || 0) > 0),
    [severityCounts]
  );

  useEffect(() => {
    if (severityFilter && (severityCounts[severityFilter] || 0) < 1) {
      setSeverityFilter("");
    }
  }, [severityFilter, severityCounts]);

  const thSx = {
    backgroundColor: t.colors.borderLight,
    borderBottom: `1px solid ${t.colors.border}`,
    fontFamily: t.fontFamily,
    color: t.colors.textMuted,
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    py: 1.1,
    px: 1.5,
    verticalAlign: "middle",
  };

  return (
    <Box sx={{ fontFamily: t.fontFamily, width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
        }}
      >
        <FormControl size="small" variant="outlined" sx={{ minWidth: 200, maxWidth: 280 }}>
          <Select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => (
              <Box component="span" sx={{ fontSize: "0.8125rem", color: t.colors.text }}>
                {severityMenuLabel(selected)}
              </Box>
            )}
            sx={{
              fontSize: "0.8125rem",
              backgroundColor: t.colors.card,
              borderRadius: t.radius.sm,
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>
              {severityMenuLabel("")}
            </MenuItem>
            {severitiesWithItems.map((sev) => (
              <MenuItem key={sev} value={sev} sx={{ fontSize: "0.8125rem" }}>
                {severityMenuLabel(sev)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <InputBase
          value={titleQuery}
          onChange={(e) => setTitleQuery(e.target.value)}
          placeholder="Filter by title or check ID"
          fullWidth
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: t.colors.textMuted }} />
            </InputAdornment>
          }
          sx={{
            flex: "1 1 220px",
            minWidth: 200,
            maxWidth: { sm: 480 },
            fontSize: "0.8125rem",
            backgroundColor: t.colors.card,
            border: `1px solid ${t.colors.border}`,
            borderRadius: t.radius.sm,
            px: 1,
            py: 0.5,
            fontFamily: t.fontFamily,
            "&:focus-within": { borderColor: t.colors.accent },
          }}
        />
      </Box>
      <TableContainer
        sx={{
          width: "100%",
          maxHeight: 440,
        }}
      >
        <Table size="small" stickyHeader sx={{ width: "100%", tableLayout: "fixed" }} aria-label="Security remediations">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: "20%", minWidth: 120 }}>Severity</TableCell>
              <TableCell sx={{ ...thSx, width: "58%" }}>Title</TableCell>
              <TableCell sx={{ ...thSx, width: "22%", minWidth: 110 }}>View remediation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: t.colors.textMuted, fontSize: "0.8125rem" }}>
                  No remediations match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((item, idx) => {
                const sev = securitySeverityDisplay(item.severity);
                const checkId = item?.check_id || item?.checkId || "";
                return (
                  <TableRow
                    key={`${checkId}-${idx}`}
                    hover
                    sx={{ "&:last-of-type td": { borderBottom: `1px solid ${t.colors.borderLight}` } }}
                  >
                    <TableCell
                      align="left"
                      sx={{
                        fontFamily: t.fontFamily,
                        verticalAlign: "top",
                        borderBottom: `1px solid ${t.colors.borderLight}`,
                        py: 1.25,
                        px: 1.5,
                      }}
                    >
                      {sev ? (
                        <Chip
                          size="small"
                          label={sev.label}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            height: 24,
                            color: sev.color,
                            backgroundColor: alpha(sev.color, 0.12),
                            border: `1px solid ${alpha(sev.color, 0.4)}`,
                            fontFamily: t.fontFamily,
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: "0.75rem", color: t.colors.textMuted }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: t.fontFamily,
                        verticalAlign: "top",
                        borderBottom: `1px solid ${t.colors.borderLight}`,
                        py: 1.25,
                        px: 1.5,
                        wordBreak: "break-word",
                      }}
                    >
                      {checkId ? (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            color: t.colors.textMuted,
                            fontFamily: "ui-monospace, monospace",
                            mb: 0.35,
                          }}
                        >
                          {checkId}
                        </Typography>
                      ) : null}
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: t.colors.text, lineHeight: 1.4 }}>
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontFamily: t.fontFamily,
                        verticalAlign: "top",
                        borderBottom: `1px solid ${t.colors.borderLight}`,
                        py: 1.25,
                        px: 1.5,
                      }}
                    >
                      {item.url ? (
                        <RemediationPreviewLink url={item.url} onOpenPreview={onOpenRemediationPreview} />
                      ) : (
                        <Typography sx={{ fontSize: "0.8rem", color: t.colors.textMuted }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** "MigrateToGraviton" -> "Migrate To Graviton" */
function formatCloudWasteActionLabel(key) {
  if (!key) return "—";
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Cloud waste: formatted total; hover shows est. monthly savings split by action */
function CloudWasteCell({ row }) {
  if (row.cloud_waste !== true) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Typography sx={{ fontSize: "0.75rem", fontFamily: t.fontFamily, color: t.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>
          —
        </Typography>
      </Box>
    );
  }

  const total = row.cloud_waste_savings_total;
  const rawMap = row.cloud_waste_savings_by_action;
  const entries =
    rawMap && typeof rawMap === "object"
      ? Object.entries(rawMap)
          .map(([k, v]) => [k, Number(v)])
          .filter(([, n]) => Number.isFinite(n) && n > 0)
          .sort((a, b) => b[1] - a[1])
      : [];

  const display = formatCost(total);

  const tooltipContent = (
    <Box sx={{ fontFamily: t.fontFamily }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.borderLight,
        }}
      >
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: t.colors.text }}>Est. Monthly Savings</Typography>
        <Typography sx={{ fontSize: "0.72rem", color: t.colors.textMuted, mt: 0.25 }}>
          Cloud waste by recommended action
        </Typography>
      </Box>
      <Box sx={{ px: 2, py: 1.25, maxWidth: 280 }}>
        {entries.length > 0 ? (
          <>
            {entries.map(([key, amt]) => (
              <Typography key={key} sx={{ fontSize: "0.75rem", color: t.colors.textMuted, mb: 0.35 }}>
                {formatCloudWasteActionLabel(key)}: {formatCost(amt)}
              </Typography>
            ))}
            {entries.length > 1 && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: t.colors.text,
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: `1px solid ${t.colors.borderLight}`,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                Monthly total: {formatCost(total)}
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontVariantNumeric: "tabular-nums" }}>{display}</Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Tooltip
        title={tooltipContent}
        placement="top-start"
        enterDelay={200}
        leaveDelay={0}
        componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            fontVariantNumeric: "tabular-nums",
            color: t.colors.text,
            cursor: "default",
            display: "inline-block",
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </Box>
  );
}

/** Security column: per-severity counts; click opens the same content as the former tooltip in a modal (when remediations exist). */
function SecurityCell({ row, onOpenDetails }) {
  const remediations = Array.isArray(row.security_remediations) ? row.security_remediations : [];
  const hasRemediations = remediations.length > 0;
  const bySeverity = row.security_failed_checks_by_severity || {};

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, width: "100%", minWidth: 0, alignItems: "flex-start" }}>
      {SECURITY_SEVERITY_ORDER.map((sev) => {
        const count =
          Number(
            bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
          ) || 0;
        if (count <= 0) return null;
        const label = sev.charAt(0) + sev.slice(1).toLowerCase(); // CRITICAL -> Critical
        return (
          <Typography
            key={sev}
            sx={{
              fontSize: "0.75rem",
              fontFamily: t.fontFamily,
              color: SECURITY_SEVERITY_COLORS[sev] || t.colors.text,
            }}
          >
            {label} ({count})
          </Typography>
        );
      })}
      {(!bySeverity ||
        SECURITY_SEVERITY_ORDER.every((sev) => {
          const count =
            Number(
              bySeverity && typeof bySeverity === "object" ? bySeverity[sev] : 0
            ) || 0;
          return count <= 0;
        })) && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontFamily: t.fontFamily,
            color: t.colors.textMuted,
          }}
        >
          —
        </Typography>
      )}
    </Box>
  );

  if (!hasRemediations) {
    return content;
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpenDetails?.(row);
      }}
      aria-label="Open security remediations"
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        cursor: "pointer",
        m: 0,
        p: 0.75,
        width: "100%",
        maxWidth: "100%",
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.sm,
        backgroundColor: t.colors.borderLight,
        font: "inherit",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": {
          borderColor: t.colors.accent,
          backgroundColor: t.colors.card,
          boxShadow: `0 0 0 1px ${t.colors.accent}40`,
        },
        "&:focus-visible": {
          outline: `2px solid ${t.colors.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      {content}
    </Box>
  );
}

/**
 * Account detail content: KPIs, service filter, resources table.
 * Used by Summary page (with accountNumber from dropdown) and by /summary/account/:id route.
 * When accounts/onAccountChange/getAccountLabel are provided, header shows only account dropdown (top right).
 */
export default function SummaryAccountDetailContent({
  accountNumber,
  showBackLink = false,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  onAccountChange,
  getAccountLabel,
  headerOnly = false,
  severity = "",
  onSeverityChange,
  framework = "",
  onFrameworkChange,
  frameworks = [],
  moduleFilters = DEFAULT_MODULE_FILTERS,
  kpiTableFilterLabel = "",
  onClearKpiTableFilter,
  /** When `onSelectedServiceChange` is set, service filter is controlled by the parent (e.g. Summary chart bar clicks). */
  selectedService: selectedServiceProp,
  onSelectedServiceChange,
}) {
  const fallbackComplianceIcon = getComplianceIcon("aws");
  const history = useHistory();
  const [details, setDetails] = useState([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsPageSize, setDetailsPageSize] = useState(25);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [internalSelectedService, setInternalSelectedService] = useState("");
  const isServiceControlled = typeof onSelectedServiceChange === "function";
  const selectedService = isServiceControlled ? (selectedServiceProp ?? "") : internalSelectedService;
  const setSelectedService = useCallback(
    (value) => {
      if (isServiceControlled) onSelectedServiceChange(value);
      else setInternalSelectedService(value);
    },
    [isServiceControlled, onSelectedServiceChange]
  );
  const [tableSearch, setTableSearch] = useState("");
  const [sortBy, setSortBy] = useState("arn");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedRow, setSelectedRow] = useState(null);
  const [complianceDialogRow, setComplianceDialogRow] = useState(null);
  const [securityDialogRow, setSecurityDialogRow] = useState(null);
  /** Top of compliance dialog: filter list to one framework, or "" for all. */
  const [complianceFrameworkFilter, setComplianceFrameworkFilter] = useState("");
  /** Per expanded framework section: filter nested rows by control ID (`fwKey` → value). */
  const [complianceControlFilterByFw, setComplianceControlFilterByFw] = useState({});
  /** Which framework groups in the compliance dialog are expanded (multi-expand). */
  const [complianceDialogExpandedFws, setComplianceDialogExpandedFws] = useState(() => new Set());
  /** http(s) URL for the right-side iframe preview (portaled, same idea as Offline CUR manual). */
  const [remediationPreviewUrl, setRemediationPreviewUrl] = useState(null);

  const openRemediationPreview = useCallback((url) => {
    if (isHttpUrlString(url)) setRemediationPreviewUrl(String(url).trim());
  }, []);

  const closeRemediationPreview = useCallback(() => {
    setRemediationPreviewUrl(null);
  }, []);

  useEffect(() => {
    if (!remediationPreviewUrl) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRemediationPreview();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [remediationPreviewUrl, closeRemediationPreview]);

  /** Stable across array reference churn — avoids fetchDetails/useEffect re-running every render. */
  const moduleFiltersKey = useMemo(() => {
    if (!Array.isArray(moduleFilters) || moduleFilters.length === 0) return "";
    return [...moduleFilters].filter(Boolean).sort().join("\0");
  }, [moduleFilters]);

  useLayoutEffect(() => {
    setDetailsPage((p) => (p === 1 ? p : 1));
  }, [moduleFiltersKey, severity, framework, accountNumber, selectedService]);

  useEffect(() => {
    if (!accountNumber) return;

    let cancelled = false;

    (async () => {
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const detailsPath = `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/details`;
        const sp = new URLSearchParams();
        sp.set("page", String(detailsPage));
        sp.set("page_size", String(detailsPageSize));
        if (selectedService) sp.set("service", selectedService);
        if (severity) sp.set("severity", severity);
        if (framework) sp.set("framework", framework);
        if (Array.isArray(moduleFilters)) {
          moduleFilters.forEach((m) => {
            if (m) sp.append("module", m);
          });
        }
        const response = await get(`${detailsPath}?${sp.toString()}`);
        if (cancelled) return;
        const status = response?.status;
        if (status >= 200 && status < 300) {
          const items = response?.items ?? [];
          setDetails(Array.isArray(items) ? items : []);
          setDetailsTotal(response?.total ?? items.length);
        } else {
          setDetailsError(getApiErrorMessage(response) || "Failed to load details");
        }
      } catch (err) {
        if (cancelled) return;
        const errData = err?.data ?? err?.response?.data;
        setDetailsError(getApiErrorMessage(errData) || err?.message || "Failed to load details");
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `moduleFilters` from closure; `moduleFiltersKey` avoids unstable `[]` reference retriggering fetch
  }, [
    accountNumber,
    detailsPage,
    detailsPageSize,
    selectedService,
    severity,
    framework,
    moduleFiltersKey,
  ]);

  const fetchServices = useCallback(async () => {
    if (!accountNumber) return;
    try {
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/summary`
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const list = response?.services ?? response?.items ?? (Array.isArray(response) ? response : []);
        setServicesList(Array.isArray(list) ? list : []);
      } else {
        setServicesList([]);
      }
    } catch {
      setServicesList([]);
    }
  }, [accountNumber]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleChangePage = (_, newPage) => setDetailsPage(newPage + 1);
  const handleChangeRowsPerPage = (e) => {
    setDetailsPageSize(parseInt(e.target.value, 10));
    setDetailsPage(1);
  };

  const filteredAndSortedDetails = useMemo(() => {
    let list = [...details];
    const q = (tableSearch || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const arn = (row.arn || "").toLowerCase();
        const service = (row.service || "").toLowerCase();
        const region = (row.region || "").toLowerCase();
        const modules = Array.isArray(row.modules) ? row.modules.join(" ").toLowerCase() : "";
        return arn.includes(q) || service.includes(q) || region.includes(q) || modules.includes(q);
      });
    }
    if (Array.isArray(moduleFilters) && moduleFilters.length > 0) {
      list = list.filter((row) => rowMatchesModuleClientFilter(row, moduleFilters));
    }
    list.sort((a, b) => {
      if (sortBy === "compliance_frameworks") {
        const af = complianceFrameworksCount(a);
        const bf = complianceFrameworksCount(b);
        let cmp = af - bf;
        if (cmp === 0) cmp = (Number(a.compliance_failed_total) || 0) - (Number(b.compliance_failed_total) || 0);
        if (cmp === 0) cmp = String(a.arn || "").localeCompare(String(b.arn || ""));
        return sortOrder === "asc" ? cmp : -cmp;
      }

      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === "arn") {
        aVal = (a.arn || "").toLowerCase();
        bVal = (b.arn || "").toLowerCase();
      } else if (sortBy === "cloud_waste") {
        aVal = a.cloud_waste === true ? Number(a.cloud_waste_savings_total) || 0 : 0;
        bVal = b.cloud_waste === true ? Number(b.cloud_waste_savings_total) || 0 : 0;
      } else if (sortBy === "compliance_control_id_count") {
        aVal = countRemediationControlIds(a);
        bVal = countRemediationControlIds(b);
      } else if (sortBy === "cost_unblended_mtd") {
        aVal = Number.parseFloat(a.cost_unblended_mtd);
        bVal = Number.parseFloat(b.cost_unblended_mtd);
        if (Number.isNaN(aVal)) aVal = 0;
        if (Number.isNaN(bVal)) bVal = 0;
      } else if (sortBy === "security_failed_checks") {
        aVal = Number(a.security_failed_checks) || 0;
        bVal = Number(b.security_failed_checks) || 0;
      } else if (sortBy === "snapshot_date") {
        aVal = a.snapshot_date || "";
        bVal = b.snapshot_date || "";
      } else {
        aVal = (aVal ?? "").toString().toLowerCase();
        bVal = (bVal ?? "").toString().toLowerCase();
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [details, tableSearch, sortBy, sortOrder, moduleFiltersKey, moduleFilters]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const pageKpis = useMemo(() => {
    let securityFailed = 0;
    let complianceFailed = 0;
    let costMtd = 0;
    filteredAndSortedDetails.forEach((row) => {
      securityFailed += Number(row.security_failed_checks) || 0;
      complianceFailed += Number(row.compliance_failed_total) || 0;
      const c = row.cost_unblended_mtd;
      if (c != null && c !== "") costMtd += Number(c) || 0;
    });
    return { securityFailed, complianceFailed, costMtd };
  }, [filteredAndSortedDetails]);

  const complianceDialogRowArn = complianceDialogRow?.arn ?? null;

  const complianceDialogRemediations = useMemo(() => {
    if (!complianceDialogRow || !Array.isArray(complianceDialogRow.compliance_remediations)) {
      return [];
    }
    const list = complianceDialogRow.compliance_remediations;
    const fw = (complianceFrameworkFilter || "").trim();
    if (!fw) return list;
    return list.filter((item) => sameFrameworkValue(item?.framework, fw));
  }, [complianceDialogRow, complianceFrameworkFilter]);

  const complianceTopFrameworkOptions = useMemo(
    () => dialogFrameworkListFromRow(complianceDialogRow),
    [complianceDialogRow]
  );

  const toggleComplianceDialogFramework = useCallback((fwKey) => {
    setComplianceDialogExpandedFws((prev) => {
      const next = new Set(prev);
      if (next.has(fwKey)) next.delete(fwKey);
      else next.add(fwKey);
      return next;
    });
  }, []);

  /** New ARN opened: start collapsed. Use `arn` (not row object reference) so details re-fetch does not reset. */
  useEffect(() => {
    if (!complianceDialogRowArn) {
      setComplianceDialogExpandedFws(new Set());
      return;
    }
    setComplianceDialogExpandedFws(new Set());
  }, [complianceDialogRowArn]);

  /** When remediation data changes, keep expanded groups that still exist (e.g. after refetch). */
  useEffect(() => {
    if (!complianceDialogRowArn) return;
    setComplianceDialogExpandedFws((prev) => {
      const currentKeys = new Set(
        groupComplianceRemediationsByFramework(complianceDialogRemediations).map(([k]) => k)
      );
      const next = new Set();
      prev.forEach((k) => {
        if (currentKeys.has(k)) next.add(k);
      });
      return next;
    });
  }, [complianceDialogRowArn, complianceDialogRemediations]);

  /** Drop per-framework control filters for framework keys that are no longer in the list. */
  useEffect(() => {
    if (!complianceDialogRowArn) return;
    setComplianceControlFilterByFw((prev) => {
      const valid = new Set(
        groupComplianceRemediationsByFramework(complianceDialogRemediations).map(([k]) => k)
      );
      const next = {};
      Object.keys(prev).forEach((k) => {
        if (valid.has(k)) next[k] = prev[k];
      });
      return next;
    });
  }, [complianceDialogRowArn, complianceDialogRemediations]);

  const renderHeader = () => {
    if (headerOnly && typeof onAccountChange === "function") {
      return (
        <FormControl
          size="small"
          fullWidth
          sx={{
            minWidth: 220,
            "& .MuiOutlinedInput-root": {
              fontFamily: t.fontFamily,
              fontSize: "0.875rem",
              backgroundColor: t.colors.card,
              borderRadius: t.radius.md,
              "& fieldset": { borderColor: t.colors.border },
              "&:hover fieldset": { borderColor: t.colors.textMuted },
              "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
            },
          }}
        >
          <InputLabel id="detail-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
            View account
          </InputLabel>
          <Select
            labelId="detail-account-label"
            id="detail-account-select"
            value={accountNumber || ""}
            label="View account"
            onChange={(e) => onAccountChange(e.target.value)}
            sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 1.5,
                  borderRadius: t.radius.md,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  maxHeight: 320,
                  "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                },
              },
            }}
          >
            {accountsLoading ? (
              <MenuItem value="" disabled>Loading…</MenuItem>
            ) : accountsError ? (
              <MenuItem value="" disabled>{accountsError}</MenuItem>
            ) : (
              accounts.map((acc, i) => {
                const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                if (!num) return null;
                return (
                  <MenuItem key={num || i} value={num}>
                    {typeof getAccountLabel === "function" ? getAccountLabel(acc) : num}
                  </MenuItem>
                );
              })
            )}
            {!accountsLoading && !accountsError && accounts.length === 0 && (
              <MenuItem value="" disabled>No accounts</MenuItem>
            )}
          </Select>
        </FormControl>
      );
    }

    if (showBackLink) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography
            component="span"
            onClick={() => history.push("/summary")}
            sx={{
              color: t.colors.accent,
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": { color: t.colors.accentHover, textDecoration: "underline" },
            }}
          >
            ← Back to ARN Insight
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const handleOpenComplianceDetails = (row) => {
    setRemediationPreviewUrl(null);
    setComplianceDialogRow(row);
    setComplianceFrameworkFilter("");
    setComplianceControlFilterByFw({});
  };

  const handleCloseComplianceDetails = () => {
    setRemediationPreviewUrl(null);
    setComplianceDialogRow(null);
    setComplianceFrameworkFilter("");
    setComplianceControlFilterByFw({});
  };

  const handleComplianceTopFrameworkChange = (e) => {
    setComplianceFrameworkFilter(e.target.value);
    setComplianceControlFilterByFw({});
  };

  const handleOpenSecurityDetails = useCallback((row) => {
    setRemediationPreviewUrl(null);
    setSecurityDialogRow(row);
  }, []);

  const handleCloseSecurityDetails = useCallback(() => {
    setRemediationPreviewUrl(null);
    setSecurityDialogRow(null);
  }, []);

  const tableCellSx = {
    fontFamily: t.fontFamily,
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${t.colors.border}`,
    py: 1,
    px: 1.75,
  };
  const headerSx = {
    ...tableCellSx,
    backgroundColor: t.colors.borderLight,
    fontWeight: 600,
    color: t.colors.textMuted,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  if (!accountNumber) {
    return (
      <Box sx={{ fontFamily: t.fontFamily }}>
        {renderHeader()}
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem" }}>
            {headerOnly
              ? "Select an account from the dropdown above to view details."
              : "Select an account to view details."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ fontFamily: t.fontFamily }}>
      {renderHeader()}

      {servicesList.length > 0 && (
        <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: t.colors.text,
                fontFamily: t.fontFamily,
                mb: 1,
              }}
            >
              Filter ARNs table by service
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="All"
                size="small"
                onClick={() => {
                  setSelectedService("");
                  setDetailsPage(1);
                }}
                sx={{
                  fontFamily: t.fontFamily,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  borderRadius: t.radius.sm,
                  bgcolor: !selectedService
                    ? t.colors.accent
                    : t.colors.borderLight,
                  color: !selectedService ? "#fff" : t.colors.text,
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: !selectedService
                      ? t.colors.accentHover
                      : "#e4e4e7",
                  },
                }}
              />
              {servicesList.map((item, i) => {
                const name = item?.service ?? item?.name ?? String(i);
                const total = item?.arns_total ?? item?.total ?? 0;
                const isActive = selectedService === name;
                return (
                  <Chip
                    key={i}
                    label={`${name}: ${total}`}
                    size="small"
                    onClick={() => {
                      setSelectedService(isActive ? "" : name);
                      setDetailsPage(1);
                    }}
                    sx={{
                      fontFamily: t.fontFamily,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      borderRadius: t.radius.sm,
                      bgcolor: isActive
                        ? t.colors.accent
                        : t.colors.borderLight,
                      color: isActive ? "#fff" : t.colors.text,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isActive
                          ? t.colors.accentHover
                          : "#e4e4e7",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
      )}

      {detailsError && (
        <Box sx={{ p: 2, mb: 2, borderRadius: t.radius.md, bgcolor: t.colors.destructiveBg, border: `1px solid ${t.colors.destructiveBorder}` }}>
          <Typography sx={{ fontSize: "0.875rem", color: t.colors.destructive, fontFamily: t.fontFamily }}>{detailsError}</Typography>
        </Box>
      )}

      {detailsLoading && (
        <Box sx={{ display: "flex", alignItems: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: t.colors.accent }} />
          <Typography sx={{ ml: 2, color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading ARNs...</Typography>
        </Box>
      )}

      {!detailsLoading && !detailsError && (
        <Box sx={{ backgroundColor: t.colors.card, borderRadius: t.radius.lg, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, overflow: "hidden" }}>
          {/* Client-side search + severity filter for table only */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${t.colors.border}`,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
              <SearchIcon sx={{ color: t.colors.textMuted, fontSize: 18 }} />
              <InputBase
                placeholder="Search by ARN, service, region, modules..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  maxWidth: 360,
                  fontSize: "0.8125rem",
                  fontFamily: t.fontFamily,
                  "& input": { py: 0.5 },
                }}
              />
              {tableSearch && (
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  {filteredAndSortedDetails.length} of {details.length} on this page
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="span"
                  sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                >
                  Severity:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={severity || ""}
                    onChange={(e) => {
                      const val = e.target.value || "";
                      if (typeof onSeverityChange === "function") {
                        onSeverityChange(val);
                      }
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.8125rem",
                      height: 32,
                      fontFamily: t.fontFamily,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                    }}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value="CRITICAL">Critical</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {Array.isArray(frameworks) && frameworks.length > 0 && typeof onFrameworkChange === "function" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}
                  >
                    Framework:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <Select
                      value={framework || ""}
                      onChange={(e) => {
                        const val = e.target.value || "";
                        onFrameworkChange(val);
                      }}
                      displayEmpty
                      sx={{
                        fontSize: "0.8125rem",
                        height: 32,
                        fontFamily: t.fontFamily,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                      }}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {frameworks.map((fw, idx) => {
                        const name = fw?.framework || fw?.name || fw || "";
                        if (!name) return null;
                        const iconSrc = getComplianceIcon(name);
                        return (
                          <MenuItem key={`${name}-${idx}`} value={name}>
                            {iconSrc && (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt={name}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: 1,
                                  objectFit: "contain",
                                  mr: 1,
                                }}
                              />
                            )}
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {kpiTableFilterLabel && typeof onClearKpiTableFilter === "function" && (
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    alignSelf: "center",
                    minHeight: 28,
                    maxWidth: 220,
                    px: 1,
                    pr: 1.25,
                    py: 0.25,
                    borderRadius: t.radius.sm,
                    border: `1px solid ${t.colors.border}`,
                    backgroundColor: t.colors.borderLight,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      color: t.colors.textMuted,
                      fontFamily: t.fontFamily,
                      lineHeight: 1.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={kpiTableFilterLabel}
                  >
                    {kpiTableFilterLabel}
                  </Typography>
                  <IconButton
                    type="button"
                    aria-label="Clear KPI filter"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearKpiTableFilter();
                    }}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      minHeight: 18,
                      p: 0,
                      backgroundColor: t.colors.card,
                      border: `1px solid ${t.colors.border}`,
                      boxShadow: t.shadow,
                      color: t.colors.textMuted,
                      "&:hover": {
                        backgroundColor: t.colors.background,
                        color: t.colors.text,
                      },
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 11 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table
              stickyHeader
              size="medium"
              sx={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: 1000,
                fontFamily: t.fontFamily,
              }}
            >
              <TableHead>
                <TableRow>
                  {DETAIL_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || "left"}
                      sx={{
                        ...headerSx,
                        width: col.width,
                        minWidth: col.minWidth,
                        maxWidth: col.width,
                        boxSizing: "border-box",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        verticalAlign: "bottom",
                      }}
                      onClick={() => handleSort(col.id)}
                    >
                      <Tooltip title={col.desc || ""} placement="top" arrow>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            flexDirection: col.labelSubtitle ? "column" : "row",
                            alignItems: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
                            gap: col.labelSubtitle ? 0.15 : 0.25,
                            justifyContent: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
                            width: col.align === "center" || col.align === "right" ? "100%" : "auto",
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.25,
                            }}
                          >
                            {col.label}
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: t.colors.textMuted, ml: 0.25 }} />
                            {sortBy === col.id &&
                              (sortOrder === "asc" ? (
                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                              ))}
                          </Box>
                          {col.labelSubtitle ? (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.65rem",
                                fontWeight: 500,
                                color: t.colors.textMuted,
                                lineHeight: 1.2,
                                textTransform: "none",
                                letterSpacing: "0.02em",
                              }}
                            >
                              {col.labelSubtitle}
                            </Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={DETAIL_TABLE_COLUMNS.length} sx={{ ...tableCellSx, py: 6, textAlign: "center", color: t.colors.textMuted }}>
                      {tableSearch ? "No rows match your search on this page." : "No ARNs"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedDetails.map((row, i) => (
                    <TableRow
                      key={row.arn ?? i}
                      selected={selectedRow?.arn === row.arn}
                      onClick={() => setSelectedRow(selectedRow?.arn === row.arn ? null : row)}
                      sx={{
                        "& td": tableCellSx,
                        "&:hover": { bgcolor: t.colors.background },
                        cursor: "pointer",
                      }}
                    >
                      {DETAIL_TABLE_COLUMNS.map((col) => {
                        const align = col.align || "left";
                        const cellBase = {
                          ...tableCellSx,
                          width: col.width,
                          maxWidth: col.width,
                          minWidth: col.minWidth,
                          boxSizing: "border-box",
                          verticalAlign: "middle",
                          overflow: "hidden",
                        };
                        if (col.id === "arn") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, verticalAlign: "top" }}>
                              <Tooltip title={row.arn || ""} placement="top-start">
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "0.8125rem",
                                    fontFamily: "monospace",
                                    color: t.colors.text,
                                    display: "block",
                                    whiteSpace: "normal",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {row.arn || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        }
                        if (col.id === "service") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, color: t.colors.text, fontWeight: 500 }}>
                              {row.service ?? "—"}
                            </TableCell>
                          );
                        }
                        if (col.id === "region") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, color: t.colors.textMuted }}>
                              {row.region ?? "—"}
                            </TableCell>
                          );
                        }
                        if (col.id === "security_failed_checks") {
                          return (
                            <TableCell key={col.id} align={align} sx={cellBase}>
                              <SecurityCell row={row} onOpenDetails={handleOpenSecurityDetails} />
                            </TableCell>
                          );
                        }
                        if (col.id === "compliance_frameworks") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily }}>
                              <ComplianceCell
                                row={row}
                                onOpenDetails={handleOpenComplianceDetails}
                                onOpenRemediationPreview={openRemediationPreview}
                              />
                            </TableCell>
                          );
                        }
                        if (col.id === "compliance_control_id_count") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>
                              <ControlIdCell row={row} onOpenDetails={handleOpenComplianceDetails} />
                            </TableCell>
                          );
                        }
                        if (col.id === "cloud_waste") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily }}>
                              <CloudWasteCell row={row} />
                            </TableCell>
                          );
                        }
                        if (col.id === "cost_unblended_mtd") {
                          return (
                            <TableCell key={col.id} align={align} sx={{ ...cellBase, fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>
                              {formatCost(row.cost_unblended_mtd)}
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Summary-style footer: Showing X–Y of Z, rows per page, Previous/Next */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${t.colors.border}`,
              backgroundColor: t.colors.card,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              borderRadius: "0 0 12px 12px",
            }}
          >
            <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontWeight: 400, fontFamily: t.fontFamily }}>
              Showing {detailsTotal === 0 ? 0 : (detailsPage - 1) * detailsPageSize + 1}–
              {Math.min(detailsPage * detailsPageSize, detailsTotal)} of {detailsTotal}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontFamily: t.fontFamily }}>
                Rows per page
              </Typography>
              <FormControl size="small" sx={{ minWidth: 56 }}>
                <Select
                  value={detailsPageSize}
                  onChange={(e) => {
                    setDetailsPageSize(Number(e.target.value));
                    setDetailsPage(1);
                  }}
                  sx={{
                    fontSize: "0.8125rem",
                    height: 32,
                    fontFamily: t.fontFamily,
                    color: t.colors.text,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n} sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage <= 1}
                onClick={() => setDetailsPage((p) => p - 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage * detailsPageSize >= detailsTotal}
                onClick={() => setDetailsPage((p) => p + 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={!!complianceDialogRow}
        onClose={handleCloseComplianceDetails}
        maxWidth="md"
        fullWidth
        sx={
          remediationPreviewUrl
            ? {
                "& .MuiDialog-container": { justifyContent: "center", overflowX: "hidden" },
              }
            : undefined
        }
        PaperProps={{
          sx: {
            borderRadius: t.radius.lg,
            boxShadow:
              "0 24px 48px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.04)",
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            overflow: "visible",
            ...(remediationPreviewUrl
              ? { mr: { xs: 0, md: `${24 + REMEDIATION_PREVIEW_PANEL_WIDTH_PX}px` } }
              : {}),
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: t.fontFamily,
            fontSize: "0.9375rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: t.fontFamily,
                fontSize: "0.9375rem",
                fontWeight: 600,
              }}
            >
              Compliance details
            </Typography>
            {complianceDialogRow?.arn && (
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: t.colors.textMuted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={complianceDialogRow.arn}
              >
                {complianceDialogRow.arn}
              </Typography>
            )}
          </Box>
          <IconButton
            edge="end"
            aria-label="Close"
            onClick={handleCloseComplianceDetails}
            sx={{
              ml: 1,
              color: t.colors.textMuted,
              "&:hover": { color: t.colors.text },
            }}
          >
            {/* Close icon kept simple to match shadcn-style dialogs */}
            <span style={{ fontSize: 18 }}>×</span>
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            px: 2,
            pt: 2.25,
            pb: 2.5,
            backgroundColor: t.colors.background,
          }}
        >
          {complianceDialogRow && (() => {
            const renderRemediationForItem = (item) => {
              if (item?.url) {
                return (
                  <RemediationPreviewLink
                    url={item.url}
                    onOpenPreview={openRemediationPreview}
                    sx={{ fontSize: "0.78rem" }}
                  />
                );
              }
              return (
                <Typography sx={{ fontFamily: t.fontFamily, fontSize: "0.78rem", color: t.colors.text }}>
                  {item?.title || "—"}
                </Typography>
              );
            };

            const nestedThSx = {
              fontFamily: t.fontFamily,
              fontSize: "0.7rem",
              fontWeight: 600,
              color: t.colors.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              backgroundColor: t.colors.borderLight,
              borderBottom: `1px solid ${t.colors.border}`,
              py: 0.75,
              px: 1.25,
            };
            const nestedTdSx = {
              fontFamily: t.fontFamily,
              fontSize: "0.78rem",
              py: 1,
              px: 1.25,
              borderBottom: `1px solid ${t.colors.borderLight}`,
            };

            const groups = groupComplianceRemediationsByFramework(complianceDialogRemediations);
            const hasAnySourceRemediations =
              Array.isArray(complianceDialogRow.compliance_remediations) &&
              complianceDialogRow.compliance_remediations.length > 0;

            return (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    mt: 0.5,
                    mb: 1.5,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="compliance-dlg-fw" sx={{ fontFamily: t.fontFamily, fontSize: "0.78rem" }}>
                      Framework
                    </InputLabel>
                    <Select
                      labelId="compliance-dlg-fw"
                      label="Framework"
                      value={complianceFrameworkFilter}
                      onChange={handleComplianceTopFrameworkChange}
                      MenuProps={COMPLIANCE_DIALOG_SELECT_MENU_PROPS}
                      sx={{ fontFamily: t.fontFamily, fontSize: "0.8rem" }}
                    >
                      <MenuItem value="">
                        <em>All frameworks</em>
                      </MenuItem>
                      {complianceTopFrameworkOptions.map((fw) => {
                        const iconSrc = getComplianceIcon(fw) || fallbackComplianceIcon;
                        return (
                          <MenuItem key={fw} value={fw}>
                            <Box
                              component="img"
                              src={iconSrc}
                              alt={fw || "aws"}
                              sx={{ width: 18, height: 18, borderRadius: 1, objectFit: "contain", mr: 1, verticalAlign: "middle" }}
                            />
                            {fw}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
               
                <TableContainer
                  sx={{
                    maxHeight: 400,
                    borderRadius: t.radius.md,
                    border: `1px solid ${t.colors.border}`,
                    backgroundColor: t.colors.card,
                    overflow: "auto",
                  }}
                >
                  {!hasAnySourceRemediations ? (
                    <Box
                      sx={{
                        fontFamily: t.fontFamily,
                        fontSize: "0.8rem",
                        color: t.colors.textMuted,
                        textAlign: "center",
                        py: 3,
                        px: 2,
                      }}
                    >
                      No compliance remediations for this resource.
                    </Box>
                  ) : complianceDialogRemediations.length === 0 ? (
                    <Box
                      sx={{
                        fontFamily: t.fontFamily,
                        fontSize: "0.8rem",
                        color: t.colors.textMuted,
                        textAlign: "center",
                        py: 3,
                        px: 2,
                      }}
                    >
                      No remediations for the selected framework. Choose &quot;All frameworks&quot; or a different
                      framework.
                    </Box>
                  ) : (
                    groups.map(([fwKey, items]) => {
                      const expanded = complianceDialogExpandedFws.has(fwKey);
                      const selectedCid = (complianceControlFilterByFw[fwKey] || "").trim();
                      let controlRows = expandRemediationItemsToControlRows(items);
                      if (selectedCid) {
                        controlRows = controlRows.filter((r) => sameControlIdValue(r.controlId, selectedCid));
                      }
                      const countLabel =
                        controlRows.length === 1 ? "1 control" : `${controlRows.length} controls`;
                      const showFwIcon = fwKey !== "—" && String(fwKey).trim() !== "";
                      const iconSrc = showFwIcon ? getComplianceIcon(fwKey) || fallbackComplianceIcon : null;
                      return (
                        <Box
                          key={fwKey}
                          sx={{
                            borderBottom: `1px solid ${t.colors.borderLight}`,
                            "&:last-of-type": { borderBottom: "none" },
                          }}
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            aria-expanded={expanded}
                            onClick={() => toggleComplianceDialogFramework(fwKey)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleComplianceDialogFramework(fwKey);
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                              px: 1.5,
                              py: 1.1,
                              cursor: "pointer",
                              backgroundColor: t.colors.borderLight,
                              borderBottom: `1px solid ${t.colors.borderLight}`,
                              "&:hover": { backgroundColor: t.colors.card },
                            }}
                          >
                            {expanded ? (
                              <ExpandLessIcon sx={{ color: t.colors.textMuted, fontSize: 22, flexShrink: 0 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ color: t.colors.textMuted, fontSize: 22, flexShrink: 0 }} />
                            )}
                            {iconSrc ? (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt=""
                                sx={{ width: 20, height: 20, borderRadius: 1, objectFit: "contain", flexShrink: 0 }}
                              />
                            ) : null}
                            <Typography
                              sx={{
                                fontFamily: t.fontFamily,
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: t.colors.text,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              {fwKey}
                            </Typography>
                            <Chip
                              size="small"
                              label={countLabel}
                              sx={{ fontFamily: t.fontFamily, fontWeight: 600, fontSize: "0.65rem", height: 22 }}
                            />
                          </Box>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ backgroundColor: t.colors.background, borderTop: "none" }}>
                              <Box
                                sx={{ px: 1.5, pt: 1.25, pb: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <FormControl size="small" sx={{ minWidth: 200, maxWidth: 320 }}>
                                  <InputLabel
                                    id={`compliance-dlg-cid-${fwKey}`}
                                    sx={{ fontFamily: t.fontFamily, fontSize: "0.78rem" }}
                                  >
                                    Control ID
                                  </InputLabel>
                                  <Select
                                    labelId={`compliance-dlg-cid-${fwKey}`}
                                    label="Control ID"
                                    value={complianceControlFilterByFw[fwKey] ?? ""}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setComplianceControlFilterByFw((prev) => ({ ...prev, [fwKey]: v }));
                                    }}
                                    MenuProps={COMPLIANCE_DIALOG_SELECT_MENU_PROPS}
                                    sx={{ fontFamily: t.fontFamily, fontSize: "0.8rem" }}
                                  >
                                    <MenuItem value="">
                                      <em>All</em>
                                    </MenuItem>
                                    {controlIdOptionsFromRemediationItems(items).map((cid) => {
                                      const v = String(cid);
                                      return (
                                        <MenuItem key={v} value={v}>
                                          {v}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              </Box>
                              <Box sx={{ px: 0, py: 0 }}>
                                {controlRows.length === 0 ? (
                                  <Box
                                    sx={{
                                      fontFamily: t.fontFamily,
                                      fontSize: "0.78rem",
                                      color: t.colors.textMuted,
                                      px: 1.5,
                                      py: 2,
                                    }}
                                  >
                                    No control IDs match this filter. Select &quot;All&quot; or another control.
                                  </Box>
                                ) : (
                                  <Table
                                    size="small"
                                    sx={{ tableLayout: "fixed", width: "100%" }}
                                    aria-label={`Controls for ${fwKey}`}
                                  >
                                    <TableHead>
                                      <TableRow>
                                        <TableCell width="32%" sx={nestedThSx}>
                                          Control ID
                                        </TableCell>
                                        <TableCell sx={nestedThSx}>Remediation</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {controlRows.map((r, rIdx) => (
                                        <TableRow key={`${fwKey}-ctrl-${r.controlId}-${rIdx}`} hover>
                                          <TableCell sx={nestedTdSx}>{r.controlId}</TableCell>
                                          <TableCell sx={nestedTdSx}>{renderRemediationForItem(r.item)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })
                  )}
                </TableContainer>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseComplianceDetails}
            sx={{
              textTransform: "none",
              fontFamily: t.fontFamily,
              fontSize: "0.8rem",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!securityDialogRow}
        onClose={handleCloseSecurityDetails}
        maxWidth="lg"
        fullWidth
        sx={
          remediationPreviewUrl
            ? {
                "& .MuiDialog-container": { justifyContent: "center", overflowX: "hidden" },
              }
            : undefined
        }
        PaperProps={{
          sx: {
            borderRadius: t.radius.lg,
            boxShadow:
              "0 24px 48px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.04)",
            border: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
            overflow: "hidden",
            ...(remediationPreviewUrl
              ? { mr: { xs: 0, md: `${24 + REMEDIATION_PREVIEW_PANEL_WIDTH_PX}px` } }
              : {}),
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: t.fontFamily,
            fontSize: "0.9375rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${t.colors.border}`,
            backgroundColor: t.colors.card,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: t.fontFamily,
                fontSize: "0.9375rem",
                fontWeight: 600,
              }}
            >
              Security remediations
            </Typography>
            {securityDialogRow?.arn && (
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: t.colors.textMuted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={securityDialogRow.arn}
              >
                {securityDialogRow.arn}
              </Typography>
            )}
          </Box>
          <IconButton
            edge="end"
            aria-label="Close"
            onClick={handleCloseSecurityDetails}
            sx={{
              ml: 1,
              color: t.colors.textMuted,
              "&:hover": { color: t.colors.text },
            }}
          >
            <span style={{ fontSize: 18 }}>×</span>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, overflow: "auto" }}>
          {securityDialogRow ? (
            <SecurityRemediationsDialogTable
              key={String(securityDialogRow.arn || securityDialogRow.arns?.[0] || "row")}
              row={securityDialogRow}
              onOpenRemediationPreview={openRemediationPreview}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSecurityDetails}
            sx={{
              textTransform: "none",
              fontFamily: t.fontFamily,
              fontSize: "0.8rem",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {remediationPreviewUrl
        ? createPortal(
            <Paper
              component="aside"
              elevation={8}
              aria-label="Remediation document preview"
              sx={{
                position: "fixed",
                right: 0,
                top: 0,
                bottom: 0,
                left: { xs: 0, md: "auto" },
                width: { xs: "100vw", md: `${REMEDIATION_PREVIEW_PANEL_WIDTH_PX}px` },
                minWidth: 0,
                maxWidth: "100vw",
                maxHeight: "100dvh",
                zIndex: 1400,
                borderRadius: 0,
                border: `1px solid ${t.colors.border}`,
                borderTop: 0,
                borderBottom: 0,
                borderRight: 0,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                boxSizing: "border-box",
                backgroundColor: t.colors.card,
              }}
            >
              <Box
                component="header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  minHeight: 44,
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: t.colors.borderLight,
                  borderBottom: `1px solid ${t.colors.border}`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: t.colors.text,
                    fontFamily: t.fontFamily,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={remediationPreviewUrl}
                >
                  Remediation
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                  <Button
                    component="a"
                    size="small"
                    color="primary"
                    variant="outlined"
                    startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
                    href={remediationPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      fontFamily: t.fontFamily,
                    }}
                  >
                    Open in a New Tab
                  </Button>
                  <IconButton
                    size="small"
                    onClick={closeRemediationPreview}
                    aria-label="Close preview"
                    sx={{ color: t.colors.textMuted, "&:hover": { color: t.colors.text } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  flex: "1 1 0%",
                  minHeight: 0,
                  minWidth: 0,
                  width: "100%",
                  overflow: "visible",
                }}
              >
                <Box
                  component="iframe"
                  key={remediationPreviewUrl}
                  title="Remediation document"
                  src={remediationPreviewUrl}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    display: "block",
                    backgroundColor: t.colors.background,
                  }}
                />
              </Box>
            </Paper>,
            document.body
          )
        : null}
    </Box>
  );
}
